import {L} from "../../lt-ng/landcare-toolkit";
import Item = L.tree.Item;
import {checkChildren, checkParents} from "../../controllers/oe/layer-selector-util";
import {GeoAppEvents} from "../../controllers/events/geoapp_events";
import {LTServices} from "../../lt-ng/lt-services";
import IMapService = LTServices.IMapService;
import {ITimeoutService} from "angular";
import {IUsageLoggingService} from "../IUsageLoggingService";


export interface IToggleLayerOptions {
    showLegend?: boolean;
    silent?: boolean;
}

export interface ILayerTreeManipulationService {
    includeLayers(...nodes: Item[]): ng.IPromise<void>;
    toggleLayer(node: Item, onOrOff: boolean, options?: IToggleLayerOptions): ng.IPromise<void>;
    expandedNodes: Item[];
    selectedNode: Item;
    visibleScienceLayer: Item;
    treeData: Item[]; // may not be initialised when you access it
    treeDataPromise: ng.IPromise<Item[]>;
    expandNode(node: Item, expand: boolean): void;
}

export class LayerTreeManipulationService implements ILayerTreeManipulationService {
    public static $inject = [
        'landcareMapService',
        '$rootScope',
        '$timeout',
        'usageLoggingService',
        '$q'
    ];

    public constructor(protected mapService: IMapService,
                       protected $rootScope: ng.IRootScopeService,
                       protected $timeout: ITimeoutService,
                       protected usageLoggingService: IUsageLoggingService) {
        $rootScope.$on(GeoAppEvents.treeChange, (event, updateExpanded) => {
            this.treeDataPromise.then((treeData) => {
                if (updateExpanded) {
                    this.expandedNodes = this.getExpandedNodes(treeData);
                }
            })

        });
    }

    selectedNode = null;
    expandedNodes = [];
    visibleScienceLayer = null;
    get treeData() {
        return this.mapService.treeData;
    }

    _promise: ng.IPromise<Item[]>;

    get treeDataPromise() {
        this._promise = this._promise || this.mapService.promise.then(() => {
            return this.mapService.treeData;
        });

        return this._promise;
    }

    expandNode(node: L.tree.Item, expand = true) {
        node.expanded = expand;

        const included = this.expandedNodes.includes(node);

        if (expand && !included) {
            this.expandedNodes.push(node);
        } else if (!expand && included) {
            this.expandedNodes.splice(this.expandedNodes.indexOf(node), 1);
        }
    }

    getExpandedNodes(nodes: any, expandAll: boolean = false): any {
        if (!nodes || nodes.length == 0) {
            return [];
        }
        let expandedNodes = [];
        for (let node of nodes) {
            if (expandAll || node.expanded) {
                expandedNodes.push(node);
                expandedNodes = expandedNodes.concat(this.getExpandedNodes(node.children, expandAll));
            }
        }
        return expandedNodes;
    }


    includeLayers(...nodes: L.tree.Item[]): ng.IPromise<void> {
        return this.mapService.promise.then(() => {
            let change = false;
            nodes.forEach(l => {
                change = change || (!l.extraState.metaLayerSelector.checked);
                l.extraState.metaLayerSelector.checked = true;
                if (change) {
                    checkChildren(l, true);
                    let root: L.tree.Item = l;
                    while (root.parent) {
                        root = root.parent;
                    }
                    checkChildren(root, true);
                    checkParents(l);
                }
            });

            this.$rootScope.$broadcast(GeoAppEvents.treeChange, change);
        });
    }

    toggleLayer(node: L.tree.Item,
                  selected: boolean,
                  options: IToggleLayerOptions = {}): ng.IPromise<void> {
        return this.mapService.promise.then(() => {
            const { silent, showLegend } = options;

            const changed = node.checked != selected;
            node.checked = selected;
            node.click(node);

            this.selectedNode = node;
            this.expandNode(node);

            if (!silent) this.$rootScope.$broadcast(GeoAppEvents.layerToggled, node, {showLegend});

            let newScienceLayer = L.tree.getVisibleScienceLayer(node.rootItems);
            if (newScienceLayer != this.visibleScienceLayer) {
                this.visibleScienceLayer = newScienceLayer;
                if (!silent) this.$rootScope.$broadcast('scienceLayer', { oldValue : this.visibleScienceLayer, newValue : newScienceLayer });
            }

            // Log the event
            if (changed && !silent) this.usageLoggingService.logAction("TOGGLE_LAYER");
        });
    }
}